import React from "react";
import { Trans } from "react-i18next";
import PropTypes from "prop-types";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormControl from "@mui/material/FormControl";
import FormLabel from "@mui/material/FormLabel";
import { SFStack } from "../grid";
import SFTypography from "../typography";

const SFRadioGroup = ({
  name,
  value,
  label,
  labelVariant = "formElementText",
  labelColor,
  size = "medium",
  direction = "row",
  radios,
  defaultValue,
  onChange,
  optionsInNewRow,
  alignLabel,
}) => {
  return (
    <FormControl>
      <SFStack
        direction={optionsInNewRow ? "column" : "row"}
        spacing={1}
        alignItems={optionsInNewRow ? "flex-start" : "center"}
      >
        {label && (
          <FormLabel
            sx={(theme) => ({
              ...theme.typography[labelVariant],
              ...(labelColor && { color: theme.palette[labelColor].main }),
            })}
            id={`${name}-label`}
          >
            {label}
          </FormLabel>
        )}

        <RadioGroup
          sx={(theme) => ({
            "& .MuiFormControlLabel-label": {
              color: theme.palette.grey400.main,
            },
            "& .Mui-checked + .MuiFormControlLabel-label": {
              color: theme.palette.grey450.main,
            },
          })}
          row={direction === "row"}
          aria-labelledby={`${name}-label`}
          name={name}
          onChange={onChange}
          defaultValue={defaultValue ?? radios[0].value}
          value={value}
        >
          {radios.map((radio) => (
            <FormControlLabel
              {...(alignLabel === "top" && {
                sx: {
                  alignItems: "flex-start",
                  justifyContent: "flex-start",
                  "& .MuiFormControlLabel-label": {
                    marginTop: "11px",
                  },
                },
              })}
              key={radio.value}
              componentsProps={{ typography: { variant: "formElementText" } }}
              value={radio.value}
              disabled={radio.disabled}
              control={
                <Radio
                  sx={(theme) => ({
                    color: theme.palette.grey450.main,
                    "&.Mui-checked": {
                      color: theme.palette.blue400.main,
                    },
                  })}
                  size={size}
                />
              }
              label={radio.label}
            />
          ))}
        </RadioGroup>
      </SFStack>
    </FormControl>
  );
};

SFRadioGroup.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string,
  size: PropTypes.oneOf(["small", "medium"]),
  color: PropTypes.oneOf([
    "default",
    "primary",
    "secondary",
    "error",
    "info",
    "success",
    "warning",
  ]),
  direction: PropTypes.oneOf(["row", "vertical"]),
  radios: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.oneOfType([PropTypes.string, PropTypes.objectOf(Trans)])
        .isRequired,
      value: PropTypes.any.isRequired,
      disabled: PropTypes.bool,
    }).isRequired
  ),
  defaultValue: PropTypes.any,
  onChange: PropTypes.func.isRequired,
  optionsInNewRow: PropTypes.bool,
  alignLabel: PropTypes.oneOf(["top"]),
  labelVariant: SFTypography.propTypes.variant,
  labelColor: PropTypes.string,
};

export default SFRadioGroup;
